import { COUNTRY_ECOTAX_TYPE, IOrder, IOrderItem } from '@wyz/types';
import {
  Button,
  Card,
  Col,
  Flex,
  FormatDate,
  FormatDeliveryDate,
  Icon,
  InformationalLegends,
  SoftBadge,
  Text,
  Title,
  Tooltip,
} from '@wyz/ui';
import {
  DateUtils,
  ORDER_STATUSES,
  ORDER_SUPPLIER_TYPE_NAME_ENUM,
  ORDER_TYPE_CODE,
} from '@wyz/utils';
import { isNil, sumBy, uniq } from 'lodash-es';
import * as React from 'react';
import { useManageEnums } from '~/common/api';
import { useConfig, Trans, useLocale } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import {
  CartTotalSection,
  OrdersDeliveryItem,
  OrdersServiceItem,
  OrdersTyreItem,
} from '~/purchase/components';
import {
  ORDER_STATUS_LABEL_LOOKUP,
  TRACKING_STATUS_LABEL_LOOKUP,
} from '~/purchase/translators';
import { OrderHelpers, SERVICE_EAN } from '~/purchase/helpers';
import { useState } from 'react';
import { PdfModal } from '~/common/components';

type Props = {
  order: IOrder;
  customer: IUserCustomer;
  displayConfiguration?: {
    tracking: boolean;
  };
};

const DEFAULT_DISPLAY: NonNullable<Props['displayConfiguration']> = {
  tracking: true,
};
const DetailView: React.FC<Props> = ({
  order,
  customer,
  displayConfiguration = DEFAULT_DISPLAY,
}) => {
  const { locale } = useLocale();

  const { tenantCode, apiUrl } = useConfig();
  const enums = useManageEnums({
    getOrderTypes: { enabled: true },
  });
  const [pdf, setPdf] = useState<string | null>(null);

  const isCompleteWheelOrder = order.type === ORDER_TYPE_CODE.H;
  const ecotaxType = isCompleteWheelOrder
    ? COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED
    : order.ecotaxType;

  const withEcoTaxPrice = (orderItem: IOrderItem): IOrderItem =>
    ecotaxType !== COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED
      ? {
          ...orderItem,
          ecoTaxPrice: order.ecoTaxLines.find(
            (ecoTaxItem) => ecoTaxItem.ean === orderItem.ean,
          )?.priceCustomer,
        }
      : orderItem;

  const wheelTyreLines = order.wheelTyreLines ?? [];
  const productLines = [...order.tyreLines, ...wheelTyreLines];

  return (
    <>
      <Col sm={4}>
        <Card style={{ minHeight: 140 }}>
          <Card.Body className='space-y-2'>
            <Flex>
              <Title heading='lg' weight='bold' className='text-truncate'>
                <Trans i18nKey='app.purchase.orders.table.order_number' />{' '}
                {' : '}
                {order.orderReference}
              </Title>
            </Flex>
            <Flex>
              <SoftBadge
                bg={
                  OrderHelpers.ORDER_STATUS_COLOR_LOOKUP[order.logisticStatusId]
                }
                pill
              >
                <Trans
                  i18nKey={ORDER_STATUS_LABEL_LOOKUP[order.logisticStatusId]}
                />
              </SoftBadge>
            </Flex>
            <Flex>
              <Flex>
                {order.printable && (
                  <Button
                    id='print-shipping-bill'
                    variant='tertiary-blue'
                    size='sm'
                    onClick={() =>
                      setPdf(
                        `${apiUrl}/offers/api/tenants/${tenantCode}/customers/${customer.id}/orders/${order.orderReference}/purchase-order/print`,
                      )
                    }
                  >
                    <Trans i18nKey='app.purchase.orders.detail.print_order_bill' />
                    <Icon icon='print' size='xs' />
                  </Button>
                )}
              </Flex>
            </Flex>
          </Card.Body>
        </Card>
      </Col>
      <Col sm={4}>
        <Card style={{ minHeight: 140 }}>
          <Card.Body className='space-y-2'>
            <InformationalLegends.Container>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.order_date' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='ordered-at'>
                  <FormatDate
                    date={order.orderedAt}
                    format={DateUtils.DATE_FORMAT.DMY}
                    locale={locale}
                  />
                </InformationalLegends.Value>
              </InformationalLegends.Row>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.estimated_delivery_date' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='delivered-at'>
                  <FormatDeliveryDate
                    orderType={order.type}
                    backorderContent={
                      <Trans i18nKey='app.common.offer_type.backorder' />
                    }
                    date={order.dateDelivered}
                    format={DateUtils.DATE_FORMAT.DMY}
                    locale={locale}
                  />
                </InformationalLegends.Value>
              </InformationalLegends.Row>
            </InformationalLegends.Container>
            {order.printable &&
              order.logisticStatusId >= ORDER_STATUSES.DELIVERY_PROGRESS &&
              ![
                ORDER_STATUSES.CANCELLED_BY_CLIENT as number,
                ORDER_STATUSES.CANCELLED_BY_SUPPLIER as number,
                ORDER_STATUSES.CANCELLED_BY_WYZ as number,
              ].includes(order.logisticStatusId) &&
              order.supplierType ===
                ORDER_SUPPLIER_TYPE_NAME_ENUM.DISTRIBUTOR_WYZ && (
                <Flex>
                  <Button
                    id='print-shipping-bill'
                    variant='tertiary-blue'
                    size='sm'
                    onClick={() =>
                      setPdf(
                        `${apiUrl}/offers/api/tenants/${tenantCode}/customers/${customer.id}/orders/${order.orderReference}/shipping-notice/print`,
                      )
                    }
                  >
                    <Trans i18nKey='app.purchase.orders.detail.print_shipping_bill' />{' '}
                    <Icon icon='print' size='xs' />
                  </Button>
                </Flex>
              )}
          </Card.Body>
        </Card>
      </Col>
      <Col sm={4}>
        <Card style={{ minHeight: 140 }}>
          <Card.Body className='space-y-2'>
            <InformationalLegends.Container>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.supplier' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='supplier-name'>
                  {order.supplierName}
                </InformationalLegends.Value>
              </InformationalLegends.Row>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.purchase.orders.table.type' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='order-type'>
                  <Tooltip
                    label={
                      order.type
                        ? enums.getOrderTypes.get(order.type)?.label
                        : ''
                    }
                    enabled={Boolean(order.type)}
                  >
                    <SoftBadge bg='primary' pill>
                      {order.type || '-'}
                    </SoftBadge>
                  </Tooltip>
                </InformationalLegends.Value>
              </InformationalLegends.Row>
              <InformationalLegends.Row>
                <InformationalLegends.Label>
                  <Trans i18nKey='app.common.user' />
                </InformationalLegends.Label>
                <InformationalLegends.Value id='orderer-name'>
                  {order.ordererName}
                </InformationalLegends.Value>
              </InformationalLegends.Row>
            </InformationalLegends.Container>
          </Card.Body>
        </Card>
      </Col>
      <Col sm={12} className='mt-3'>
        <Title heading='lg' weight='bold'>
          <Trans i18nKey='app.purchase.orders.detail.title' />
        </Title>
      </Col>
      <Col sm={8} className='mt-3'>
        {order.tyreLines.map((orderItem, index) => {
          return (
            <Card key={index} className='mt-2'>
              <Card.Body>
                <OrdersTyreItem
                  customer={customer}
                  orderItem={withEcoTaxPrice(orderItem)}
                  ecotaxType={ecotaxType}
                  showBf={!isCompleteWheelOrder}
                  currency={order.currency}
                />
              </Card.Body>
            </Card>
          );
        })}
        {wheelTyreLines.map((orderItem, index) => {
          return (
            <Card key={`wcw-${index}`} className='mt-2'>
              <Card.Body>
                <OrdersTyreItem
                  customer={customer}
                  orderItem={withEcoTaxPrice(orderItem)}
                  ecotaxType={ecotaxType}
                  showBf={!isCompleteWheelOrder}
                  currency={order.currency}
                />
              </Card.Body>
            </Card>
          );
        })}
        {order.serviceLines?.map((serviceItem, index) => {
          return (
            <Card key={index} className='mt-2'>
              <Card.Body>
                <OrdersServiceItem
                  serviceItem={serviceItem}
                  currency={order.currency}
                />
              </Card.Body>
            </Card>
          );
        })}
        {!isCompleteWheelOrder &&
          order.ecotaxType === COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED &&
          Boolean(order.ecoTaxLines.length) &&
          order.ecoTaxLines?.map((ecoTaxItem, index) => {
            return (
              <Card key={index} className='mt-2'>
                <Card.Body>
                  <OrdersServiceItem
                    serviceItem={ecoTaxItem}
                    currency={order.currency}
                  />
                </Card.Body>
              </Card>
            );
          })}

        {order.deliveryLines
          ?.filter(
            (serviceItem) =>
              serviceItem.ean === SERVICE_EAN.J0_DELIVERY ||
              (!isNil(serviceItem.priceCustomer) &&
                serviceItem.priceCustomer !== 0),
          )
          .map((serviceItem, index) => {
            return (
              <Card key={index} className='mt-2'>
                <Card.Body>
                  <OrdersDeliveryItem order={order} serviceItem={serviceItem} />
                </Card.Body>
              </Card>
            );
          })}

        {displayConfiguration.tracking && (
          <>
            {(order.trackingUnavailable ||
              Boolean(order.trackings?.length)) && (
              <Title heading='lg' weight='bold' className='my-3'>
                <Trans i18nKey='app.purchase.orders.delivery_tracking.title' />
              </Title>
            )}
            {order.trackingUnavailable && (
              <Card>
                <Card.Body className='space-y-2'>
                  <Text weight='semi-bold' heading='normal'>
                    <Trans i18nKey='app.purchase.order_detail.tracking_helper.title' />
                  </Text>
                  {order.supplierOrderReference && (
                    <Text weight='semi-bold' color='secondary-gray'>
                      <Trans
                        i18nKey='app.purchase.order_detail.tracking_helper.subtitle'
                        values={{
                          reference: order.supplierOrderReference,
                        }}
                      />
                    </Text>
                  )}
                </Card.Body>
              </Card>
            )}
            {order.trackings?.map((trackingItem, index) => (
              <Card key={index} className='mt-2'>
                <Card.Body>
                  <Flex justifyContent='between'>
                    <Text
                      color='tertiary'
                      weight='bold'
                      heading='normal'
                      className='flex-grow-1'
                    >
                      <Trans i18nKey='app.purchase.order_detail.tracking.package' />{' '}
                      {index + 1}
                    </Text>
                    <Flex className='space-x-4'>
                      <SoftBadge
                        bg={
                          OrderHelpers.TRACKING_STATUS_COLOR_LOOKUP[
                            trackingItem.status
                          ] ?? 'secondary'
                        }
                        pill
                      >
                        <Trans
                          i18nKey={
                            TRACKING_STATUS_LABEL_LOOKUP[trackingItem.status]
                          }
                          defaults={trackingItem.status}
                        />
                      </SoftBadge>
                      <a
                        href={trackingItem.url}
                        target='_blank'
                        rel='noreferrer'
                      >
                        <Text color='tertiary' weight='semi-bold' heading='xs'>
                          <Icon icon='map-marker' size='xs' />{' '}
                          <Trans i18nKey='app.purchase.orders.table.tracking' />
                        </Text>
                      </a>
                    </Flex>
                  </Flex>
                </Card.Body>
              </Card>
            ))}
          </>
        )}
      </Col>
      <Col sm={4} className='mt-3'>
        <>
          <CartTotalSection
            values={{
              referencesCount: uniq(productLines.map((item) => item.ean))
                .length,
              articlesCount: sumBy(productLines, 'quantity'),
              servicesCount: sumBy(order.deliveryLines, 'quantity'),
              totalExclTaxProduct: sumBy(productLines, 'excludedTaxesAmount'),
              totalExclTaxDelivery: sumBy(
                order.deliveryLines,
                'excludedTaxesAmount',
              ),
              totalExclTaxEcoTax: sumBy(
                order.ecoTaxLines,
                'excludedTaxesAmount',
              ),
              taxesAmount: order.taxesAmount,
              totalExclTax: order.excludedTaxesAmount,
              inclTaxTotal: order.includedTaxesAmount,
            }}
            privileges={{
              ecotaxType,
            }}
            mode='ORDER'
            customer={customer}
            currency={order.currency}
          />
          <Card className='mt-2'>
            <Card.Body className='bg-neutral-grey-1'>
              <Flex>
                <span className='text-royal-blue'>
                  <Icon icon='chat-line' size='xs' />
                </span>
                <Title heading='lg' weight='bold' className='mb-3' wrap>
                  <Trans i18nKey='app.common.comment_of_customer' />
                </Title>
              </Flex>
              <div>{order.comment || ''}</div>
            </Card.Body>
          </Card>
          {order.supplierComment && (
            <Card className='mt-2'>
              <Card.Body className='bg-neutral-grey-1'>
                <Flex>
                  <span className='text-royal-blue'>
                    <Icon icon='chat-line' size='xs' />
                  </span>
                  <Title heading='lg' weight='bold' className='mb-3' wrap>
                    <Trans i18nKey='app.purchase.cart_page.table.supplier_comment' />
                  </Title>
                </Flex>
                <div>{order.supplierComment}</div>
              </Card.Body>
            </Card>
          )}
        </>
      </Col>
      {pdf !== null && <PdfModal url={pdf} onClose={() => setPdf(null)} />}
    </>
  );
};

export default DetailView;
